import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import ContractPopup from '../ContractPopup';

// Dropbox finishes generating the signed PDF a beat after the DJ signs - in
// practice ~15-20s. Until it does, signatureRequestFilesAsFileUrl 4xxs, so the
// card sits in 'Processing' and polls rather than offering a download that
// fails silently.
const POLL_INTERVAL_MS = 6000;
const MAX_POLL_ATTEMPTS = 30; // ~3 minutes before we stop and hand it to the DJ

const SUPPORT_EMAIL = 'djs@djsatthepolls.org';

// The optimistic date ContractPopup writes on signing and the one Quickbase
// hands back aren't in the same format, so the card would reformat itself the
// moment polling resolves. Parse against both explicitly - never let moment
// fall back to the JS Date() parser.
const formatSignedDate = (value) => {
  if (!value) return '';
  const parsed = moment(value, ['MM-DD-YYYY', 'YYYY-MM-DD'], true);
  return parsed.isValid() ? parsed.format('MM-DD-YYYY') : value;
};

// Contract widget. The status shown here is Quickbase's (a lookup off the
// Contract record that the Dropbox webhooks write), never a local guess - the
// one exception is the optimistic 'Processing' that ContractPopup sets the
// instant the embedded iframe reports a signature, which the backend confirms.
export default function ContractSection({ dj, elections, setElections }) {
  const djElection = elections?.[0]?.dj_elections?.[0];
  const contractStatus = djElection?.dropbox_contract_status;
  const signatureId = djElection?.dropbox_signature_id;
  const signatureRequestId = djElection?.dropbox_signature_request_id;

  const isSigned = contractStatus === 'Signed';
  const isProcessing = contractStatus === 'Processing';

  const [contractUrl, setContractUrl] = useState('');
  const [showContractPopup, setShowContractPopup] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const fetchContractStatus = useCallback(async () => {
    if (!signatureRequestId) return null;
    const res = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/dropbox/contractStatus?signature_request_id=${signatureRequestId}`
    );
    return res.data;
  }, [signatureRequestId]);

  const applyContractStatus = useCallback((status, signedDate) => {
    setElections((prevElections) => {
      if (!prevElections?.[0]?.dj_elections?.[0]) return prevElections;
      const [first, ...rest] = prevElections;
      return [
        {
          ...first,
          dj_elections: first.dj_elections.map((election, index) =>
            index === 0
              ? {
                  ...election,
                  dropbox_contract_status: status,
                  ...(signedDate ? { dropbox_contract_signed_date: signedDate } : {})
                }
              : election
          )
        },
        ...rest
      ];
    });
  }, [setElections]);

  // Poll while processing. Clearing the interval is handled by this effect
  // re-running once applyContractStatus flips the status out of 'Processing'.
  useEffect(() => {
    if (!isProcessing || !signatureRequestId || pollExhausted) return;

    let cancelled = false;
    let attempts = 0;

    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const data = await fetchContractStatus();
        if (cancelled) return;
        if (data?.contract_status === 'Signed') {
          applyContractStatus('Signed', data.contract_signed_date);
          return;
        }
      } catch (error) {
        // A failed poll isn't worth surfacing - the next one is 6s away, and
        // running out of attempts is what the DJ actually needs to hear about.
        console.error('Error checking contract status:', error);
      }
      if (!cancelled && attempts >= MAX_POLL_ATTEMPTS) setPollExhausted(true);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isProcessing, signatureRequestId, pollExhausted, fetchContractStatus, applyContractStatus]);

  const handleCheckAgain = async () => {
    if (isChecking) return;
    setIsChecking(true);
    try {
      const data = await fetchContractStatus();
      if (data?.contract_status === 'Signed') {
        applyContractStatus('Signed', data.contract_signed_date);
      } else {
        setPollExhausted(false); // still pending - give it another round of polling
      }
    } catch (error) {
      console.error('Error checking contract status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const showSignedContract = async () => {
    if (!signatureId) {
      console.log('No contract available');
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/dropbox/getSignedContract?signature_request_id=${signatureRequestId}`
      );
      if (res.data) {
        // For signed contracts, open in a new window to avoid AWS signature issues
        window.open(res.data.signUrl, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const showContract = async () => {
    if (!signatureId) {
      console.log('No contract available');
      return;
    }
    try {
      // Reset contract state before fetching new contract
      setContractUrl('');
      setShowContractPopup(false);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/dropbox/getSignUrl?signature_request_id=${signatureId}`
      );
      if (res.data) {
        setContractUrl(res.data.signUrl);
        setShowContractPopup(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const statusLabel = isSigned ? 'Signed' : isProcessing ? 'Processing' : 'Not Signed';
  const badgeClasses = isSigned
    ? 'text-green-800 bg-green-100'
    : isProcessing
      ? 'text-blue-800 bg-blue-100'
      : 'text-yellow-800 bg-yellow-100';
  const statusTextClasses = isSigned
    ? 'text-green-600'
    : isProcessing
      ? 'text-blue-600'
      : 'text-yellow-600';

  let instructions;
  if (isSigned) {
    instructions = 'Click below to see the signed copy of your contract.';
  } else if (isProcessing && pollExhausted) {
    instructions = `This is taking longer than usual. Your signature is saved — check again in a few minutes, or email ${SUPPORT_EMAIL}.`;
  } else if (isProcessing) {
    instructions = "Your signature is in. We're preparing your signed copy — it'll appear here in a moment.";
  } else {
    instructions = 'Please review and sign your contract in the next five days so we can start making DJ assignments.';
  }

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-200 mb-6" id="contract-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            Contract Details
          </h3>
          <span
            role="status"
            aria-live="polite"
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg ${badgeClasses}`}
          >
            {isProcessing && (
              <span
                className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            )}
            {statusLabel}
          </span>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <p className="mt-1 text-sm text-gray-900">{dj?.first_name + " " + dj?.last_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <p className={`mt-1 text-sm ${statusTextClasses}`}>
                {statusLabel}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Signed Date</label>
              <p className="mt-1 text-sm text-gray-900">
                {formatSignedDate(djElection?.dropbox_contract_signed_date) || 'Not yet signed'}
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">{instructions}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {isSigned ? (
              <>
                <button
                  onClick={showSignedContract}
                  className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 shadow-sm"
                >
                  <svg className="mr-2 h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  View Signed Contract
                </button>
                <div className="sm:hidden mt-2 p-3 bg-yellow-100 border border-yellow-400 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">
                    <svg className="inline-block mr-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    On mobile, you will need to enable pop-ups or access on a desktop.
                  </p>
                </div>
              </>
            ) : isProcessing ? (
              <>
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed shadow-sm"
                >
                  <svg className="mr-2 h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Preparing your copy
                </button>
                <button
                  type="button"
                  onClick={handleCheckAgain}
                  disabled={isChecking}
                  className="inline-flex items-center justify-center px-2 py-2.5 text-sm font-medium text-primary hover:text-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-lg transition-colors duration-200 disabled:text-gray-400"
                >
                  {isChecking ? 'Checking' : 'Check again'}
                </button>
              </>
            ) : (
              <button
                onClick={showContract}
                className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 shadow-sm"
              >
                <svg className="mr-2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Contract
              </button>
            )}
          </div>
        </div>
      </div>

      {showContractPopup && (
        <ContractPopup
          url={contractUrl}
          isSigned={contractUrl.includes('getSignedContract')}
          elections={elections}
          setElections={setElections}
          signatureRequestId={signatureRequestId}
          onClose={() => {
            // Completely reset the contract state
            setShowContractPopup(false);
            setTimeout(() => {
              setContractUrl('');
            }, 100); // Small delay to ensure proper cleanup
          }}
        />
      )}
    </>
  );
}
